import { Input, Select } from 'antd';
import { Panel } from '../Panel';
import { useTranslate } from '@tolgee/react';
import { ProductListParamsKeywordType } from 'services/api/conversation';
import { useCallback, useMemo, useState } from 'react';
import { css, cx } from '@emotion/css';
import { useQuery } from '@tanstack/react-query';
import { queries } from 'services/queries';
import { useDebounce } from 'ahooks';
import { ProductInfoImage } from '../../ProductInfo/Image';
import { ProductInfoTitle } from '../../ProductInfo/Title';
import { RelatedSKU } from './RelatedSKU';

interface PureOnlyRelatedItemsProps {
  storeId: string;
  ttsToko?: boolean;
  canEdit?: boolean;
  relatedSku: Todo.RelatedSku[];
  onChange: (next: Todo.RelatedSku[]) => void;
  onQuantityChange: (index: number, quantity: number) => void;
  platform?: General.PlatformUpperCase;
}

type ProductLike = {
  externalItemId: string;
  itemName: string;
  itemPic?: string;
  itemSku?: string;
  sellerSku?: string;
  quantity: number;
  skuId?: string;
};

interface ProductOptionProps {
  item: ProductLike;
  platform?: General.PlatformUpperCase;
}

const ProductOption = (props: ProductOptionProps) => {
  const { item, platform } = props;
  const { itemName, itemPic = '', itemSku, sellerSku } = item;
  return (
    <div className={cx('min-h-[48px] max-w-[270px] flex gap-2 items-start')}>
      <ProductInfoImage imgUrl={itemPic} itemName={itemName} platform={platform} />
      <div className={cx('grid grid-cols-[1fr_auto] gap-y-1 min-w-0 flex-1 gap-x-3')}>
        <ProductInfoTitle title={itemName} url={''} className="min-w-0" copyable={false} />
        <div className="col-span-2 text-xs text-[var(--fett-text-tertiary)] flex items-center gap-1 min-w-0">
          {itemSku ? (
            <span className="truncate" title={itemSku}>
              [{itemSku}]
            </span>
          ) : null}
          {sellerSku ? (
            <span className="truncate" title={sellerSku}>
              [{sellerSku}]
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export const PureOnlyRelatedItems = (props: PureOnlyRelatedItemsProps) => {
  const {
    storeId,
    ttsToko = false,
    canEdit = true,
    relatedSku,
    onChange,
    onQuantityChange,
    platform,
  } = props;
  const { t } = useTranslate();
  const [keywordType, setKeywordType] = useState<ProductListParamsKeywordType>(
    ProductListParamsKeywordType.ITEM_NAME,
  );
  const [keyword, setKeyword] = useState('');

  const debouncedKeyword = useDebounce(keyword, {
    wait: 800,
  });

  const options = [
    {
      value: ProductListParamsKeywordType.ITEM_NAME,
      label: t('sider.商品名称'),
      simple: t('sider.商品名称'),
    },
    {
      value: ProductListParamsKeywordType.ITEM_ID,
      label: t('sider.商品ID'),
      simple: t('sider.商品ID'),
    },
    {
      value: ProductListParamsKeywordType.SKU,
      label: 'SKU',
      simple: 'SKU',
    },
    {
      value: ProductListParamsKeywordType.ITEM_NO,
      label: `${t('sider.货号')}`,
      simple: t('sider.货号'),
    },
  ];

  const { data } = useQuery({
    ...queries.products.list({
      keyword_type: keywordType,
      keyword: debouncedKeyword,
      internal_store_id: storeId,
      page_no: 1,
      page_size: 50,
      needStockRule: true,
      order_by: 'SALE' as const,
      ttsToko,
    }),
  });

  const itemList = useMemo(() => data?.itemList ?? [], [data]);

  const dataSource: ProductLike[] = useMemo(() => {
    return (itemList || []).reduce<ProductLike[]>((acc, cur) => {
      const { externalItemId, itemName, imageUrl, skuInfo } = cur;
      if (skuInfo && skuInfo.length) {
        skuInfo.forEach((sku) => {
          acc.push({
            externalItemId,
            itemName,
            itemPic: imageUrl?.[0],
            itemSku: sku.skuName,
            sellerSku: sku.sellerSku,
            quantity: 1,
            skuId: sku.skuId,
          });
        });
      } else {
        acc.push({
          externalItemId,
          itemName,
          itemPic: imageUrl?.[0],
          quantity: 1,
        });
      }
      return acc;
    }, []);
  }, [itemList]);

  const selectedValueKeys = useMemo(
    () =>
      (relatedSku || []).map((item) => {
        return `${item.itemId}___${item.skuId || ''}`;
      }),
    [relatedSku],
  );

  const handleChange = useCallback(
    (valueKeys: string[]) => {
      const keySet = new Set<string>(valueKeys);
      const existingMap = new Map(
        (relatedSku || []).map((item) => [`${item.itemId}___${item.skuId || ''}`, item]),
      );
      const next: Todo.RelatedSku[] = [];
      valueKeys.forEach((key) => {
        const [itemId, skuId = ''] = key.split('___');
        const existed = existingMap.get(key);
        if (existed) {
          next.push(existed);
          return;
        }
        const product = dataSource.find(
          (it) => it.externalItemId === itemId && (it.skuId || '') === skuId,
        );
        if (!product) {
          return;
        }
        next.push({
          itemId: product.externalItemId,
          skuId: product.skuId || '',
          skuName: product.itemSku || '',
          sellerSku: product.sellerSku || '',
          quantity: product.quantity ?? 1,
          itemName: product.itemName || '',
        });
      });
      // 追加旧的但当前未在 valueKeys 中的项（理论上不会有，但保持 append 语义的兼容性）
      (relatedSku || []).forEach((item) => {
        const key = `${item.itemId}___${item.skuId || ''}`;
        if (!keySet.has(key)) return;
      });
      console.log('next', next);
      onChange(next);
    },
    [relatedSku, dataSource, onChange],
  );

  return (
    <Panel title={t('tab.product')}>
      {canEdit && (
        <Input.Group compact className="!w-full !flex items-center !mb-2">
          <Select
            className={cx('max-w-[140px]', borderCls)}
            dropdownMatchSelectWidth={false}
            optionLabelProp="simple"
            options={options}
            value={keywordType}
            onChange={(value) => setKeywordType(value)}
            disabled={!canEdit}
          />
          <Select
            mode="multiple"
            className={cx('!inline-flex !flex-1', borderCls, fullCsl)}
            dropdownMatchSelectWidth={false}
            optionLabelProp="label"
            placeholder={t('department.请选择x', {
              x: t('tab.product'),
            })}
            filterOption={false}
            maxTagCount="responsive"
            showArrow
            value={selectedValueKeys}
            onChange={handleChange}
            onSearch={setKeyword}
            showSearch
            disabled={!canEdit}
          >
            {dataSource.map((item) => {
              const key = `${item.externalItemId}___${item.skuId || ''}`;
              return (
                <Select.Option className={optionCls} key={key} value={key} label={item.itemName}>
                  <ProductOption item={item} platform={platform} />
                </Select.Option>
              );
            })}
          </Select>
        </Input.Group>
      )}
      {relatedSku.length > 0 && (
        <div className="space-y-2">
          <RelatedSKU
            canEdit={canEdit}
            data={relatedSku}
            storeId={storeId}
            disabled={!canEdit}
            onQuantityChange={onQuantityChange}
            showArrow={false}
            platform={platform}
          />
        </div>
      )}
    </Panel>
  );
};

const borderCls = css`
  &.ant-select:not(.ant-select-disabled):hover .ant-select-selector {
    border-color: var(--fett-border-strong) !important;
  }
`;
const fullCsl = css`
  &.ant-select .ant-select-selector {
    width: 100% !important;
  }
`;
const optionCls = css`
  &.ant-select-item {
    align-items: center;
  }
`;
